import { err, ok } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';
import {
  buildSpeakersFromMappings,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { getClosestElementIndexToRightByFilter } from '@src/utility/videoTranscriptionUtils';

import type { CompositionTemplate } from '@src/components/sidepanel/CompositionSettings/types';
import type { Result } from '@src/services/common/Result';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { Artifact, ShareableImageType, Story } from '@src/types.ts/story';
import type { TranscriptElementWithViewData } from '@src/types.ts/transcript';

export interface SavePullQuoteServiceDeps {
  datoClientStore: DatoClientStore;
}

export interface SavePullQuoteInput {
  storyId: string;
  story: Story;
  selectedText: string;
  startIndex: number;
  endIndex: number;
  transcriptElements: TranscriptElementWithViewData[];
  editorName?: string;
}

export interface SavePullQuoteBatchInput {
  story: Pick<Story, 'id' | 'title' | 'storyTeller'>;
  quotes: ShareableImageType[];
  compositionId?: string;
  editorName?: string;
}

export interface SavePullQuoteBatchResult {
  savedQuotes: ShareableImageType[];
  failedCount: number;
}

export interface SavePullQuoteResult {
  savedId: string;
  speakerLabel?: string;
  speakerId?: string;
  storytellerName?: string;
  speakerHeadshot?: string;
  quote: string;
  imagefile?: Artifact;
  metadata?: Record<string, any>;
}

/**
 * Service for creating pull quote shareable images from transcript selections.
 * Handles speaker lookup, data transformation, and repository orchestration.
 */
export class SavePullQuoteService {
  private readonly _datoClientStore: DatoClientStore;
  private readonly _logger = LogManager.getLogger(
    'service:SavePullQuoteService',
  );

  constructor(deps: SavePullQuoteServiceDeps) {
    this._datoClientStore = deps.datoClientStore;
  }

  /**
   * Finds the speaker_label from the transcript elements at the given index.
   * If the start element has whitespace subtype and doesn't have a speaker_label,
   * searches for the closest non-whitespace element to the right within the selection range.
   */
  private findSpeakerLabelFromSelection(
    startIndex: number,
    endIndex: number,
    transcriptElements: TranscriptElementWithViewData[],
  ): string | undefined {
    let startElement = transcriptElements[startIndex];

    // If start element has whitespace subtype, find closest non-whitespace to the right
    if (
      !startElement?.speaker_label &&
      startElement?.subtype === 'whitespace'
    ) {
      const nonWhitespaceStartIndex = getClosestElementIndexToRightByFilter(
        startIndex,
        transcriptElements,
        (el, idx) => el.subtype !== 'whitespace' && idx <= endIndex,
      );

      if (nonWhitespaceStartIndex !== -1) {
        startElement = transcriptElements[nonWhitespaceStartIndex];
      }
    }

    return startElement?.speaker_label;
  }

  /**
   * Looks up the storyteller name from speaker mappings.
   * Returns formatted name as "Name|Title" if title exists, otherwise just "Name".
   * Falls back to story.storyTeller.name if speaker not found in mappings.
   */
  private async lookupSpeakerInfo(
    speakerLabel: string | undefined,
    storyId: string,
    defaultName: string | undefined,
  ): Promise<{
    storytellerName?: string;
    speakerHeadshot?: string;
    speakerId?: string;
  }> {
    let storytellerName = defaultName;
    let speakerHeadshot: string | undefined;
    let speakerId: string | undefined;

    if (speakerLabel && storyId) {
      const [speakerMappings, datoSpeakers] = await Promise.all([
        fetchSpeakerVoiceMappings(storyId),
        fetchDatoSpeakers(storyId, this._datoClientStore),
      ]);

      const speakers = buildSpeakersFromMappings(
        [speakerLabel],
        speakerMappings,
        datoSpeakers,
      );

      const matchedSpeaker = speakers.find(
        s => s.speakerLabel === speakerLabel,
      );

      if (matchedSpeaker?.name) {
        // Format as "Name|Title" using pipe delimiter (parsed by formatStoryteller)
        storytellerName = matchedSpeaker.title
          ? `${matchedSpeaker.name}|${matchedSpeaker.title}`
          : matchedSpeaker.name;
      }

      // Get latest headshot URL if available
      if (matchedSpeaker?.headshots?.[0]) {
        speakerHeadshot = matchedSpeaker.headshots[0];
      }

      // Capture the DatoCMS speaker record ID for linking
      if (matchedSpeaker?.id) {
        speakerId = matchedSpeaker.id;
      }
    }

    return { storytellerName, speakerHeadshot, speakerId };
  }

  /**
   * Gets the first available image from the story's artifacts.
   * Prioritizes story artifacts, then story assets, then organization artifacts.
   * Only returns images with a responsiveImage (filters out videos).
   */
  private getFirstAvailableImage(story: Story): Artifact | undefined {
    // Get images from story artifacts (filter to only those with responsiveImage)
    const storyArtifacts = (story.storyArtifacts || []).filter(
      artifact => artifact.responsiveImage,
    );

    // Get images from story assets
    const storyAssets = (story.storyAssets || []).filter(
      asset => asset.responsiveImage,
    );

    // Get images from organization artifacts (brand kit)
    const organizationArtifacts =
      story._allReferencingShowcases?.flatMap(
        (showcase: { organizationArtifacts?: Artifact[] }) =>
          (showcase.organizationArtifacts || []).filter(
            (artifact: Artifact) => artifact.responsiveImage,
          ),
      ) || [];

    // Combine all available images, prioritizing story > assets > brand kit
    const allImages = [
      ...storyArtifacts,
      ...storyAssets,
      ...organizationArtifacts,
    ];

    // Return the first available image, or undefined if none
    return allImages[0];
  }

  /**
   * Resolves the default composition template ID for pull quotes.
   * Priority: org default → first custom → first "Default" preset → first preset → first template.
   */
  resolveDefaultCompositionId(
    compositionTemplates: CompositionTemplate[],
    defaultPullQuoteTemplateId?: string,
  ): string | undefined {
    const template =
      (defaultPullQuoteTemplateId
        ? compositionTemplates.find(t => t.id === defaultPullQuoteTemplateId)
        : undefined) ||
      compositionTemplates.find(t => !t.isGlobalPreset) ||
      compositionTemplates.find(
        t => t.isGlobalPreset && t.internalName.includes('Default'),
      ) ||
      compositionTemplates.find(t => t.isGlobalPreset) ||
      compositionTemplates[0];
    return template?.id;
  }

  /**
   * Saves a batch of AI-generated pull quotes, handling partial failures gracefully.
   * Returns all successfully saved quotes and the count of failures, so the caller
   * can update UI state even when some quotes fail.
   */
  async savePullQuoteBatch(
    input: SavePullQuoteBatchInput,
  ): Promise<Result<SavePullQuoteBatchResult>> {
    const { story, quotes, compositionId, editorName } = input;

    if (!this._datoClientStore.storyRepository) {
      return err('PRECONDITION', 'Story repository not available');
    }

    if (quotes.length === 0) {
      return err('VALIDATION', 'No quotes to save');
    }

    const savedQuotes: ShareableImageType[] = [];
    let failedCount = 0;

    for (const quote of quotes) {
      try {
        const metadata =
          typeof quote.metadata === 'string'
            ? (() => {
                try {
                  return JSON.parse(quote.metadata);
                } catch {
                  return {};
                }
              })()
            : quote.metadata || {};

        const saveMetadata = {
          ...metadata,
          ...(editorName && { editedBy: editorName }),
        };

        const savedId =
          await this._datoClientStore.storyRepository.createShareableImage(
            story,
            quote.quote,
            quote.imagefile,
            quote.storytellerName,
            undefined,
            compositionId,
            saveMetadata,
            quote.speaker?.id,
          );

        if (savedId) {
          savedQuotes.push({
            ...quote,
            id: savedId,
            metadata: saveMetadata,
            _updatedAt: new Date().toISOString(),
            ...(compositionId && {
              parentComposition: { id: compositionId },
            }),
          });
        } else {
          failedCount++;
          this._logger.warn('Quote save returned no ID', {
            quoteText: quote.quote.slice(0, 50),
          });
        }
      } catch (error) {
        failedCount++;
        this._logger.error('Failed to save individual quote in batch', {
          error,
          quoteText: quote.quote.slice(0, 50),
        });
      }
    }

    this._logger.info('Pull quote batch save completed', {
      storyId: story.id,
      total: quotes.length,
      saved: savedQuotes.length,
      failed: failedCount,
    });

    return ok({ savedQuotes, failedCount });
  }

  async savePullQuote(
    input: SavePullQuoteInput,
  ): Promise<Result<SavePullQuoteResult>> {
    const {
      storyId,
      story,
      selectedText,
      startIndex,
      endIndex,
      transcriptElements,
    } = input;

    try {
      // Validate inputs
      if (!selectedText?.trim()) {
        return err('VALIDATION', 'Selected text cannot be empty');
      }

      if (!story.id) {
        return err('VALIDATION', 'Story ID is required');
      }

      if (!this._datoClientStore.storyRepository) {
        return err('PRECONDITION', 'Story repository not available');
      }

      // Find speaker_label from transcript element
      const speakerLabel = this.findSpeakerLabelFromSelection(
        startIndex,
        endIndex,
        transcriptElements,
      );

      // Look up speaker name, title, headshot, and DatoCMS record ID from mappings
      const { storytellerName, speakerHeadshot, speakerId } =
        await this.lookupSpeakerInfo(
          speakerLabel,
          storyId,
          story.storyTeller?.name,
        );

      // Get the first available image from the story's artifacts
      // This mirrors Content Studio behavior which auto-assigns images to pull quotes
      const imageFile = this.getFirstAvailableImage(story);

      if (imageFile) {
        this._logger.debug('Auto-selected image for pull quote', {
          storyId,
          imageId: imageFile.id,
          imageUrl: imageFile.url,
        });
      }

      // Build metadata with editor info
      const saveMetadata: Record<string, any> = {};
      if (input.editorName) {
        saveMetadata.editedBy = input.editorName;
      }

      // Create the shareable image with the auto-selected image (if available)
      const savedId =
        await this._datoClientStore.storyRepository.createShareableImage(
          story,
          selectedText,
          imageFile, // use auto-selected image or undefined
          storytellerName,
          undefined, // source
          undefined, // parentCompositionId
          Object.keys(saveMetadata).length > 0 ? saveMetadata : undefined,
          speakerId,
        );

      if (!savedId) {
        return err(
          'EXTERNAL',
          'Failed to create shareable image - no ID returned',
        );
      }

      this._logger.info('Pull quote saved successfully', {
        storyId,
        savedId,
        speakerLabel,
        speakerId,
        storytellerName,
        hasImage: !!imageFile,
        hasHeadshot: !!speakerHeadshot,
      });

      return ok({
        savedId,
        speakerLabel,
        speakerId,
        storytellerName,
        speakerHeadshot,
        quote: selectedText,
        imagefile: imageFile,
        metadata:
          Object.keys(saveMetadata).length > 0 ? saveMetadata : undefined,
      });
    } catch (error) {
      this._logger.error('Failed to save pull quote', {
        error,
        storyId: input.storyId,
      });
      return err(
        'UNKNOWN',
        'Failed to save pull quote',
        error && typeof error === 'object' ? error : String(error),
      );
    }
  }
}

export default SavePullQuoteService;
